import { ClickAwayListener } from '@mui/base';
import { Box, Dropdown, Menu, MenuButton, Stack, Typography } from '@mui/joy';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { attributeStatusColorMap, attributeStatusLabelKey } from 'companySettings/constants';
import { AttributeStatus, AttributeVersionType } from 'companySettings/enums';
import { IPartFinderVersion } from 'companySettings/types';
import { Button } from 'core/components';
import { Loader } from 'core/elements';
import { Color, Direction, Level, Size, Variant } from 'theme/enums';

import { VersionEntry } from './VersionEntry';

const COMPARED_VERSIONS_COUNT = 2;

interface IProps<TChanges> {
  status: AttributeStatus;
  versionNumber: number;
  versions: IPartFinderVersion<TChanges>[];
  isLoading: boolean;
  isError: boolean;
  errorMessage: string;
  formatChanges: (changes: TChanges, isPublish: boolean) => string;
  renderCompare: (
    fromVersionId: number,
    toVersionId: number,
    isOpen: boolean,
    onClose: () => void
  ) => ReactNode;
  onOpenChange: (isOpen: boolean) => void;
}

// The status pill of an open versioned record; it opens the record's versions (the working draft
// first, then the published ones, two of which can be compared). The owner loads the versions once
// the pill opens and draws the comparison.
export const VersionHistory = <TChanges,>({
  status,
  versionNumber,
  versions,
  isLoading,
  isError,
  errorMessage,
  formatChanges,
  renderCompare,
  onOpenChange,
}: IProps<TChanges>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedVersionIds, setSelectedVersionIds] = useState<number[]>([]);
  const [isCompareOpen, setIsCompareOpen] = useState(false);

  const { t } = useTranslation();

  const hasPublishedVersions = versions.some(
    (version) => AttributeVersionType.DRAFT !== version.type
  );
  // Sorted, so the same pair ticked in either order is one comparison (and one cached query).
  const [olderSelectedVersionId, newerSelectedVersionId] = [...selectedVersionIds].sort(
    (firstId, secondId) => firstId - secondId
  );
  const canCompare = COMPARED_VERSIONS_COUNT === selectedVersionIds.length;

  const handleOnOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange(open);
  };

  const onToggleVersion = (versionId: number) =>
    setSelectedVersionIds((versionIds) =>
      versionIds.includes(versionId)
        ? versionIds.filter((selectedVersionId) => selectedVersionId !== versionId)
        : [...versionIds, versionId]
    );

  const onCompare = () => {
    handleOnOpenChange(false);
    setIsCompareOpen(true);
  };

  return (
    <>
      {/* The drawer's focus trap keeps focus away from the menu, so the menu cannot close itself
          on blur: a click outside the pill and the menu closes it instead. */}
      <ClickAwayListener onClickAway={() => isOpen && handleOnOpenChange(false)}>
        <Box sx={{ alignSelf: 'flex-start' }}>
          <Dropdown open={isOpen} onOpenChange={(_, open: boolean) => handleOnOpenChange(open)}>
            {/* A pill, as the design draws the status · version. */}
            <MenuButton
              variant={Variant.SOFT}
              color={attributeStatusColorMap[status]}
              size={Size.SM}
              sx={{ borderRadius: 'xl' }}
            >
              {t('labelAttributeStatusVersion', {
                status: t(attributeStatusLabelKey[status]),
                number: versionNumber,
              })}
            </MenuButton>

            {/* Above the drawer the pill sits in: menus default to a layer below modals. */}
            <Menu
              placement="bottom-start"
              sx={{
                zIndex: (theme) => theme.zIndex.modal + 1,
                width: 460,
                maxHeight: 520,
                overflowY: 'auto',
                p: 2,
              }}
            >
              <Stack sx={{ gap: 2 }}>
                <Stack
                  direction={Direction.ROW}
                  sx={{ alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <Typography level={Level.TITLE_MD}>{t('labelVersionHistory')}</Typography>
                  <Button
                    text={t('buttonLabelCompare')}
                    variant={Variant.SOFT}
                    disabled={!canCompare}
                    onClick={onCompare}
                  />
                </Stack>

                {isLoading && <Loader size={Size.SM} />}

                {/* No gap between the entries, so each one's timeline runs into the next. */}
                {versions.length > 0 && (
                  <Stack>
                    {versions.map((version, index) => (
                      <VersionEntry
                        key={version.id}
                        version={version}
                        formatChanges={formatChanges}
                        isSelected={selectedVersionIds.includes(version.id)}
                        onToggle={() => onToggleVersion(version.id)}
                        isSelectable={!canCompare}
                        isLast={index === versions.length - 1}
                      />
                    ))}
                  </Stack>
                )}

                {isError && (
                  <Typography level={Level.TITLE_SM} color={Color.DANGER}>
                    {errorMessage}
                  </Typography>
                )}

                {!isLoading && !isError && !hasPublishedVersions && (
                  <Typography level={Level.TITLE_SM} sx={{ color: 'neutral.600' }}>
                    {t('labelNoPublishedVersions')}
                  </Typography>
                )}
              </Stack>
            </Menu>
          </Dropdown>
        </Box>
      </ClickAwayListener>

      {canCompare &&
        renderCompare(olderSelectedVersionId, newerSelectedVersionId, isCompareOpen, () =>
          setIsCompareOpen(false)
        )}
    </>
  );
};
